import { noop } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Id } from 'react-toastify';
import { useStyles } from 'uinix-ui';

import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { Popover } from '~/eds/src/components/Popover/Popover';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { INPUT_DEBOUNCE_MS } from '~/eds/src/constants/duration';
import { useDebounce } from '~/eds/src/hooks/useDebounce';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { copy } from '~/eds/src/utils/copy';
import { EntityType } from '~/enums/EntityType';
import { LocalStorageKeyType } from '~/enums/LocalStorageKeyType';
import { isActiveQuery } from '~/features/advanced-search/query-filters/QueryFilters.utils';
import { AskAnythingButton } from '~/features/ask-anything/ask-anything-button/AskAnythingButton';
import { CreditBalance } from '~/features/billing/CreditBalance/CreditBalance';
import { useCreditBalanceMessages } from '~/features/billing/CreditBalance/hooks';
import { CreditSummary } from '~/features/billing/CreditBalance/types';
import { DocumentViewer } from '~/features/document-viewer/DocumentViewer';
import { Highlight } from '~/features/document-viewer/types/highlight';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useClientId } from '~/hooks/useClientId';
import { useErrors } from '~/hooks/useErrors';
import { useLocalStorageState } from '~/hooks/useLocalStorageState';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';

import {
  useGetCreditsTooltip,
  useGetEstimatedCreditsTooltip,
} from '../billing/hooks';
import { RECOMMENDED_PUBLISH_TEST_CASE_COUNT } from '../constants';
import { testIsConfigDirty } from '../fields/utils';
import { usePrefetchDocuments } from '../hooks/usePrefetchDocuments';
import { useResolveTargetEntityDetails } from '../hooks/useResolveTargetEntityDetails';
import { PublishFieldModelModal } from '../modals/PublishFieldModelModal';
import { PublishModelVersionModal } from '../modals/PublishModelVersionModal';
import { PublishQuotaFieldModelModal } from '../modals/PublishQuotaFieldModelModal';
import { PublishQuotaModelVersionModal } from '../modals/PublishQuotaModelVersionModal';
import { BoostModal } from '../Review/ReviewBoost/BoostModal';
import { BoostTrigger } from '../Review/ReviewBoost/BoostTrigger';
import { StringMatchingLevel } from '../types/config';
import { PromptModelTestCase } from '../types/deprecated';
import {
  testIsActivePublishStatus,
  testIsNonOptimizedFieldModel,
  testIsNonOptimizedModel,
  testIsPublishingStatus,
} from '../utils/status';
import { mapTableConfigToState, testIsTableConfigDirty } from '../utils/table';
import {
  extractDocumentVersionIds,
  findTestCaseIndexByNumber,
  getNextActiveTestCase,
} from '../utils/testcase';
import {
  DEPRECATED_getModelProgressStatusChip,
  getModelProgressStatusChip,
} from '../utils/ui';
import { Configuration } from './Configuration';
import { Evaluate } from './Evaluate';
import { useGetHighlightActions } from './hooks';
import { TableDetailsPanel } from './TableDetailsPanel';

const TOAST_ID = 70257; // arbitrary number id constant for toast

export const ModelDetails = () => {
  const intl = useIntl();
  const { navigate, params, getSearchParam, setSearchParam } = useRouting();
  const { modelId = '' } = params;
  const isNew = modelId === 'new';

  const styles = useStyles();
  const dispatch = useDispatch();
  const { RETRY_ERROR } = useErrors();
  const { ERROR_ESTIMATION_MESSAGE } = useCreditBalanceMessages();

  const clientId = useClientId();
  const enableCopilot = useFlag(FlagType.DocumentXRayCopilot);
  const enableDocumentXRayBilling = useFlag(FlagType.DocumentXRayBilling);
  const enableDocumentXrayOptimize = useFlag(FlagType.DocumentXrayOptimize);
  const enablePublishIroning = useFlag(FlagType.CustomAiPublishIroning);
  const enableTableExtraction = useFlag(FlagType.CustomAiTableExtraction);
  const enableXRayLibrary = useFlag(FlagType.DocumentXRayPrebuiltModels);
  const enableXrayNewPricingModel = useFlag(
    FlagType.DocumentXrayNewPricingModel,
  );
  const isCustomAiAsyncTestCreationExecutionEnabled = useFlag(
    FlagType.CustomAiAsyncTestCreationExecution,
  );
  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);

  const isTableModel =
    enableTableExtraction && getSearchParam('type') === 'table';

  const [
    enablePublishModal,
    _toggleEnablePublishModal,
    showEnablePublishModal,
    hideEnablePublishModal,
  ] = useToggle(false);

  const [enableTogglePopover, _toggleEnablePopover, showPopover, hidePopover] =
    useToggle(false);

  const [dismissWelcomeContent, toggleDismissWelcomeContent] = useToggle();
  const [
    onConfirmDiscardChangesSearchParam,
    setOnConfirmDiscardChangesSearchParam,
  ] =
    useState<Nullable<{ key: string; value: string; pathname: RoutePathType }>>(
      null,
    );
  const [selectedConfigId, setSelectedConfigId] =
    useState<Nullable<string>>(null);
  const [isValidConfigStatus, setIsValidConfigStatus] =
    useState<Nullable<string>>(null);

  const activeTableModelVersion = useSelector(
    slices.xRay.selectors.selectActiveTableModelVersion,
  );
  const tableConfig = useSelector(
    slices.xRay.selectors.selectTableConfiguration,
  );

  const activeModelVersion = useSelector(
    slices.xRay.selectors.selectActiveModel,
  );
  const activeVersionNumber = useSelector(
    slices.xRay.selectors.selectActiveVersionNumber,
  );
  const versions = useSelector(slices.xRay.selectors.selectModelVersions);
  const config = useSelector(slices.xRay.selectors.selectConfiguration);
  const documentViewer = useSelector(
    slices.xRay.selectors.selectDocumentViewer,
  );
  const defaultHasViewedWelcomeContent = useSelector(
    slices.xRay.selectors.selectHasViewedWelcomeContent,
  );

  const query = useSelector(slices.xRay.selectors.selectQuery);
  const activeQuery = isActiveQuery(query);
  const creditSummary = useSelector(slices.xRay.selectors.selectCreditsSummary);
  const optimize = useSelector(slices.xRay.selectors.selectOptimize);

  const activeTestcaseNumber = useSelector(
    slices.xRay.selectors.selectActiveTestcaseNumber,
  );
  const activeTestCase =
    activeModelVersion?.testCases[activeTestcaseNumber - 1];

  const field = useSelector(slices.xRay.selectors.selectField);

  const { toast, isActive } = useToast();
  const toastIdRef = useRef<Id>(TOAST_ID);

  const modelName = isTableModel
    ? tableConfig.tableSpecState.name
    : activeModelVersion?.name;

  const editModelConfirmed =
    (activeModelVersion &&
      testIsNonOptimizedFieldModel(activeModelVersion, ['suspended'])) ||
    (activeTableModelVersion &&
      testIsNonOptimizedModel(activeTableModelVersion, ['suspended']));

  const disableEditOnActiveModels =
    (activeModelVersion &&
      testIsActivePublishStatus(activeModelVersion.publishStatus)) ||
    (activeTableModelVersion &&
      testIsActivePublishStatus(activeTableModelVersion.publishStatus));

  const discardChanges = () => {
    dispatch(slices.xRay.actions.resetXRay());
    dispatch(slices.xRay.actions.setActiveModelResultInfo(null));
    if (onConfirmDiscardChangesSearchParam) {
      const { key, value, pathname } = onConfirmDiscardChangesSearchParam;
      setSearchParam(key, value, { pathname });
    } else {
      navigate(RoutePathType.AutomationHubFields);
    }
  };

  const [
    updatePromptModelProgress,
    { isLoading: isUpdatingPromptModelProgress, error: updatePromptModelError },
  ] = api.endpoints.updatePromptModelProgress.useMutation();

  const { prefetchDocuments } = usePrefetchDocuments();

  const [suspendFieldModel, { isLoading: isSuspendingFieldModel }] =
    api.endpoints.suspendPromptModel.useMutation();

  const { data: progress = null } =
    api.endpoints.getPromptModelProgress.useQuery(
      {
        modelId,
      },
      {
        skip: isNew || !modelId,
      },
    );

  const entityDisplayType = isTableModel
    ? intl.formatMessage({
        id: 'Xray.modelDetails.tableEntityType',
        defaultMessage: 'Table',
        description: 'Table entity type',
      })
    : intl.formatMessage({
        id: 'Xray.modelDetails.fieldEntityType',
        defaultMessage: 'Field',
        description: 'Field entity type',
      });

  const [confirmDiscardChangesModal, showConfirmDiscardChangesModal] = useModal(
    {
      title: intl.formatMessage({
        id: 'Xray.modelDetails.discardChangesTitle',
        defaultMessage: 'Discard Changes?',
        description: 'Title for the discard changes modal',
      }),
      enableHideOnPrimaryAction: true,
      children: (
        <Text>
          <FormattedMessage
            id="Xray.modelDetails.discardChangesMessage"
            defaultMessage="You have unsaved changes. Click Discard Changes to exit without saving, or Continue Editing to stay on this page."
            description="Discard changes modal contents"
          />
        </Text>
      ),
      primaryAction: {
        text: intl.formatMessage({
          id: 'Xray.modelDetails.continueEditing',
          defaultMessage: 'Continue Editing',
          description: 'Label for the continue editing button',
        }),
      },
      secondaryActions: [
        {
          text: intl.formatMessage({
            id: 'Xray.modelDetails.discardChanges',
            defaultMessage: 'Discard Changes',
            description: 'Label for the discard changes button',
          }),
          variant: 'tertiary',
          onClick: discardChanges,
        },
      ],
      cancelText: null,
    },
  );

  const [editWarningdModal, showEditWarningModal, hideEditWarningModal] =
    useModal({
      cancelText: intl.formatMessage({
        id: 'Xray.modelDetails.editWarningCancel',
        defaultMessage: 'Cancel',
        description: 'Label for the cancel button in the edit warning modal',
      }),
      children: enableXrayNewPricingModel ? (
        <Layout preset="sections">
          <Text>
            <FormattedMessage
              id="Xray.modelDetails.editWarningMessage1"
              defaultMessage="Entering edit mode will suspend your model and stop running on unprocessed documents. You can return to this page to resume your model, or republish your model with changes."
              description="Edit warning modal contents if new pricing flag is on"
            />
          </Text>
          <StatusMessage
            message={intl.formatMessage({
              id: 'Xray.modelDetails.suspendedModelsWarning',
              defaultMessage:
                'Suspended models still count towards your model quota.',
              description: 'Warning message for suspended models',
            })}
            status="warning"
          />
        </Layout>
      ) : (
        <Text>
          <FormattedMessage
            id="Xray.modelDetails.editWarningMessage2"
            defaultMessage="When you enter edit mode, your model will be suspended and will stop running on unprocessed documents. Are you sure you want to continue? You can come back to this page to resume your model, or republish your model with your changes."
            description="Edit warning modal contents if new pricing flag is off"
          />
        </Text>
      ),
      primaryAction: {
        isLoading: isSuspendingFieldModel,
        onClick: () => {
          suspendFieldModel({ modelId })
            .then(() => {
              toast({
                message: intl.formatMessage(
                  {
                    id: 'Xray.modelDetails.suspendModelSuccessMessage',
                    defaultMessage: 'Suspended <strong>{modelName}</strong>',
                    description:
                      'Message when the model was successfully suspended. Example: modelName = "Invoice Data"',
                  },
                  {
                    modelName,
                    strong: (chunks) => `<strong>${chunks}</strong>`,
                  },
                ),
                status: 'success',
              });
              hideEditWarningModal();
            })
            .catch(() =>
              toast({
                message: intl.formatMessage(
                  {
                    id: 'Xray.modelDetails.suspendModelFailureMessage',
                    defaultMessage:
                      'Failed to suspend <strong>{modelName}</strong>. Please try again.',
                    description:
                      'Message when the model failed to suspend. Example: modelName = "Invoice Data"',
                  },
                  {
                    modelName,
                    strong: (chunks) => `<strong>${chunks}</strong>`,
                  },
                ),
                status: 'danger',
              }),
            )
            .finally(() => {
              refetchFieldModel();
            });
        },
        text: enableXrayNewPricingModel
          ? intl.formatMessage({
              id: 'Xray.modelDetails.editModelModal1',
              defaultMessage: 'Edit Model',
              description:
                'Label for the edit model button in modal when feature flag is on',
            })
          : intl.formatMessage({
              id: 'Xray.modelDetails.enterEditMode2',
              defaultMessage: 'Confirm',
              description:
                'Label for the confirm edit model button in modal when feature flag is off',
            }),
        variant: 'primary',
      },
      title: intl.formatMessage(
        {
          id: 'Xray.modelDetails.editModelModalTitle',
          defaultMessage: 'Edit {entityType} Model',
          description:
            'Title for the edit model modal. Example: entityType is either Table or Field',
        },
        { entityType: entityDisplayType },
      ),
    });

  const [
    publishWarningModal,
    showPublishWarningModal,
    hidePublishWarningModal,
  ] = useModal({
    cancelText: intl.formatMessage({
      id: 'Xray.modelDetails.publishWarningCancel',
      defaultMessage: 'Evaluate More Results',
      description: 'Label for the cancel button in the publish warning modal',
    }),
    children: (
      <Text>
        <FormattedMessage
          id="Xray.modelDetails.publishWarningMessage"
          defaultMessage="We recommend that you rate at least 10 results before publishing your model to ensure that it works across your documents."
          description="Publish warning modal contents"
        />
      </Text>
    ),
    primaryAction: {
      isLoading: isUpdatingPromptModelProgress,
      onClick: () => {
        editModelConfirmed
          ? updatePromptModelProgress({ modelId }).then(() =>
              showEnablePublishModal(),
            )
          : showEnablePublishModal();
      },
      text: intl.formatMessage({
        id: 'Xray.modelDetails.reviewModel2',
        defaultMessage: 'Review & Publish',
        description: 'Label for the review of a model prior to publishing',
      }),
      variant: 'danger',
    },

    title: intl.formatMessage({
      id: 'Xray.modelDetails.publishWarningTitle',
      defaultMessage: 'Are you sure you want to publish?',
      description: 'Title for the publish warning modal',
    }),
  });

  const [hasViewedWelcomeContent, setHasViewedWelcomeContent] =
    useLocalStorageState(
      LocalStorageKeyType.WelcomeFieldAi,
      defaultHasViewedWelcomeContent,
      {
        skipUpdateLocalStorage: !dismissWelcomeContent,
      },
    );
  const enableWelcomeContent = isNew && !hasViewedWelcomeContent;

  const {
    data: model,
    isFetching: isLoadingModel,
    refetch: refetchFieldModel,
  } = api.endpoints.getPromptModel.useQuery(
    { modelId },
    {
      skip: isNew,
    },
  );

  const { data: tableModel, isFetching: isLoadingTableModel } =
    api.endpoints.getModelResult.useQuery(
      { modelId },
      {
        skip: isNew || !isTableModel,
      },
    );

  const [
    fetchTableModelVersion,
    { currentData: tableModelVersion, isFetching: isLoadingTableModelVersion },
  ] = api.endpoints.getPromptModelVersion.useLazyQuery();

  const targetEntityFieldId: Nullable<number> =
    model?.fieldId ?? field?.id ?? null;
  const [targetEntityDetails, isResolvingTargetEntityDetails] =
    useResolveTargetEntityDetails(
      targetEntityFieldId
        ? {
            id: targetEntityFieldId.toString(),
            type: EntityType.Field,
          }
        : null,
    );

  const [
    getModelVersion,
    { currentData: modelVersion = null, isFetching: isLoadingModelVersion },
  ] = api.endpoints.DEPRECATED_getPromptModelVersion.useLazyQuery();

  const [
    getDocsInScope,
    {
      data: docsInScope = 0,
      isFetching: isFetchingDocsInScope,
      isUninitialized,
    },
  ] = api.endpoints.getDocsInScope.useLazyQuery();

  const {
    currentData: creditEstimation = 0,
    isFetching: isCreditEstimationFetching,
  } = api.endpoints.getCreditEstimation.useQueryState({
    clientId,
    estimateUnit: {
      modelId: isNew ? undefined : modelId,
      isLoadingUnitValue: isFetchingDocsInScope,
      isUninitialized,
      unit: 'document-count',
      value: docsInScope,
    },
  });

  const latestVersion = model ? Math.max(...model.versions) : 1;

  useEffect(() => {
    if (model?.versions.length) {
      dispatch(slices.xRay.actions.setActiveVersionNumber(latestVersion));
      dispatch(slices.xRay.actions.setModelVersions(model.versions));
      if (isTableModel) {
        fetchTableModelVersion({
          modelId,
          version: latestVersion,
          highlightMethod,
        });
      } else {
        fetchModelVersion(model.fieldId, latestVersion);
      }
    }
  }, [latestVersion, model]);

  useEffect(() => {
    if (tableModelVersion) {
      dispatch(
        slices.xRay.actions.setActiveTableModelVersion(tableModelVersion),
      );
      if (tableModelVersion.config && tableModel?.targetEntity?.id) {
        dispatch(
          slices.xRay.actions.setTableConfiguration(
            mapTableConfigToState({
              modelVersion: tableModelVersion,
              entityId: tableModel.targetEntity.id,
            }),
          ),
        );
        if (tableModelVersion.scope) {
          dispatch(slices.xRay.actions.setQuery(tableModelVersion.scope));
        }
        if (tableModelVersion.testCases.length) {
          dispatch(
            slices.xRay.actions.setTableTestCases(tableModelVersion.testCases),
          );
          const nextActiveTestCase = getNextActiveTestCase(
            tableModelVersion.testCases,
          );
          dispatch(
            slices.xRay.actions.setActiveTestcaseNumber(
              nextActiveTestCase?.number ?? tableModelVersion.testCases.length,
            ),
          );
        }
      }
    }
  }, [tableModel, tableModelVersion]);

  useEffect(() => {
    if (versions && !isNew) {
      const versionNumber =
        versions.length > 0 ? versions[activeVersionNumber - 1] : 1;
      if (isTableModel) {
        fetchTableModelVersion({
          modelId,
          version: versionNumber,
          highlightMethod,
        });
      } else if (field) {
        handleFetchModelVersion(field, versionNumber);
      }
    }
  }, [activeVersionNumber]);

  useEffect(() => {
    if (field && !isTableModel) {
      getDocsInScope({
        entityId: String(field.id),
        entityType: EntityType.Field,
        query: activeQuery,
      }).catch((err) => {
        toast({
          message: err?.detail.msg ?? ERROR_ESTIMATION_MESSAGE,
          status: 'danger',
        });
      });
    } else if (tableModel) {
      getDocsInScope({
        entityId: tableModel.targetEntity.id,
        entityType: EntityType.DocumentAttachedData,
        query: activeQuery,
      }).catch((err) => {
        toast({
          message: err?.detail.msg ?? ERROR_ESTIMATION_MESSAGE,
          status: 'danger',
        });
      });
    }
  }, [tableModel, field, query]);

  useEffect(() => {
    if (updatePromptModelError) {
      toast({
        message: intl.formatMessage({
          id: 'Xray.modelDetails.promptModelUpdateError',
          defaultMessage: 'Something went wrong. Please try again.',
          description: 'Error message for updating prompt model',
        }),
        status: 'danger',
      });
    }
  }, [updatePromptModelError]);

  const isLoading =
    (!isTableModel && (isLoadingModel || isLoadingModelVersion)) ||
    (isTableModel && (isLoadingTableModel || isLoadingTableModelVersion));

  const isConfigDirty = testIsConfigDirty(
    config,
    modelVersion?.config,
    activeQuery,
  );
  const isTableConfigDirty = testIsTableConfigDirty({
    activeQuery,
    config: tableConfig,
    originalConfig:
      tableModelVersion?.config && tableModel?.targetEntity?.id
        ? mapTableConfigToState({
            modelVersion: tableModelVersion,
            entityId: tableModel.targetEntity.id,
          })
        : undefined,
  });

  const disablePublishTooltip = isLoading
    ? intl.formatMessage({
        id: 'Xray.modelDetails.loadingModel',
        defaultMessage: 'Loading model…',
        description:
          'Tooltip shown when the model is loading and cannot be published',
      })
    : activeModelVersion?.publishStatus === 'completed'
      ? intl.formatMessage({
          id: 'Xray.modelDetails.alreadyPublished',
          defaultMessage: 'This model has already been published.',
          description: 'Tooltip shown when the model is already published',
        })
      : testIsPublishingStatus(activeModelVersion?.publishStatus)
        ? intl.formatMessage({
            id: 'Xray.modelDetails.currentlyPublishing',
            defaultMessage: 'This model is currently being published.',
            description:
              'Tooltip shown when the model is in the process of publishing',
          })
        : ((!isTableModel && activeModelVersion?.version !== latestVersion) ||
              (isTableModel &&
                activeTableModelVersion?.version !== latestVersion)) &&
            !isNew
          ? intl.formatMessage({
              id: 'Xray.modelDetails.onlyLatestVersion',
              defaultMessage: 'You can only publish the latest version.',
              description:
                'Tooltip shown when trying to publish a non-latest version',
            })
          : (!isTableModel && isConfigDirty) ||
              (isTableModel && isTableConfigDirty)
            ? intl.formatMessage({
                id: 'Xray.modelDetails.saveBeforePublish',
                defaultMessage: 'Please save your changes before publishing.',
                description: 'Tooltip shown when there are unsaved changes',
              })
            : enableDocumentXRayBilling && isCreditEstimationFetching
              ? intl.formatMessage({
                  id: 'Xray.modelDetails.loadingCreditEstimation',
                  defaultMessage: 'Loading credit estimation…',
                  description:
                    'Tooltip shown when credit estimation is loading',
                })
              : enableDocumentXRayBilling &&
                  creditSummary &&
                  creditEstimation > creditSummary.credits &&
                  activeModelVersion &&
                  activeModelVersion.publishStatus !== 'suspended'
                ? intl.formatMessage({
                    id: 'Xray.modelDetails.insufficientCredits',
                    defaultMessage:
                      'Estimated credits to publish exceeds credits available. You can only save the model as a draft and not publish. Adjust scope or Get More Credits in Automation Hub to publish.',
                    description:
                      'Tooltip shown when there are not enough credits to publish',
                  })
                : undefined;

  const handleCancel = () => {
    if (
      hasViewedWelcomeContent &&
      !isLoading &&
      ((!isTableModel && isConfigDirty) || (isTableModel && isTableConfigDirty))
    ) {
      showConfirmDiscardChangesModal();
    } else {
      discardChanges();
    }
  };

  const handleFetchModelVersion = useDebounce(
    (field, versionId) => {
      fetchModelVersion(field.id, versionId);
    },
    INPUT_DEBOUNCE_MS,
    [versions],
  );

  const fetchModelVersion = (fieldId: PilotId, versionNumber: number) =>
    getModelVersion({
      modelId,
      version: versionNumber,
      fieldId,
      highlightMethod,
    })
      .unwrap()
      .then((modelVersion) => {
        if (modelVersion) {
          const firstUnreviewedTestCase = modelVersion.testCases.length
            ? getNextActiveTestCase(modelVersion.testCases)
            : null;

          // Prefetch document names BEFORE updating Redux state
          if (
            isCustomAiAsyncTestCreationExecutionEnabled &&
            firstUnreviewedTestCase
          ) {
            const activeIndex = findTestCaseIndexByNumber(
              modelVersion.testCases,
              firstUnreviewedTestCase.number,
            );
            const testCasesToPrefetch =
              modelVersion.testCases.slice(activeIndex);
            const prefetchIds = extractDocumentVersionIds(testCasesToPrefetch);
            if (prefetchIds.length > 0) {
              prefetchDocuments(prefetchIds);
            }
          }

          //TODO: Clean up: remove config from state and rely on updating modelVersion.
          dispatch(slices.xRay.actions.setConfiguration(modelVersion.config));
          dispatch(
            slices.xRay.actions.setActiveModel({
              ...modelVersion,
              publishStatus: model?.publishStatus ?? null, // TODO: this is not ideal and should be handled in upstream API (but requires team to discuss how APIs should be recomposed to assemble the data we need)
            }),
          );
          if (modelVersion.testCases.length) {
            dispatch(
              slices.xRay.actions.setActiveTestcaseNumber(
                firstUnreviewedTestCase?.number ??
                  modelVersion.testCases.length,
              ),
            );
            if (
              isCustomAiAsyncTestCreationExecutionEnabled &&
              firstUnreviewedTestCase
            ) {
              dispatch(
                slices.xRay.actions.setDocumentViewer({
                  documentVersionId: Number(firstUnreviewedTestCase.entity.id),
                }),
              );
            }
          } else if (docsInScope === 0) {
            const noDocsInScopeMessage = modelVersion.libraryModelId
              ? // Models from the library have different copy based on publish status
                progress?.publishStatus === 'completed' ||
                activeModelVersion?.publishStatus === 'completed' ||
                modelVersion.publishStatus === 'completed' ||
                progress?.publishStatus === 'in_progress' ||
                activeModelVersion?.publishStatus === 'in_progress' ||
                modelVersion.publishStatus === 'in_progress'
                ? intl.formatMessage({
                    id: 'Xray.modelDetails.noDocsInScopePublishedFromLibrary',
                    defaultMessage:
                      'The model was successfully published, but there are no documents in scope.',
                    description:
                      'Toast message when a model is published from the library but there are no documents in scope',
                  })
                : intl.formatMessage({
                    id: 'Xray.modelDetails.noDocsInScopeFromLibrary',
                    defaultMessage:
                      'The model was successfully added to Automation Hub, but there are no documents in scope.',
                    description:
                      'Toast message when a model is saved from the library but there are no documents in scope',
                  })
              : intl.formatMessage({
                  id: 'Xray.modelDetails.noActiveDocumentsInScopeMessage',
                  defaultMessage:
                    'The configuration provided to the model did not yield any results. Change Scope: Try modifying your filters to adjust the scope and pull in relevant results.',
                  description:
                    'Message for the toast when no documents are in scope',
                });
            // reset the stale document viewer
            dispatch(
              slices.xRay.actions.setDocumentViewer({
                documentId: null,
                documentVersionId: null,
                activeHighlightId: undefined,
                highlights: [],
              }),
            );
            // show toast letting the user know they need to update the scope filters
            if (!isActive(TOAST_ID) && !isActive(toastIdRef.current)) {
              // prevent multiple duplicates
              toastIdRef.current = toast({
                key: TOAST_ID,
                title: !modelVersion.libraryModelId
                  ? intl.formatMessage({
                      id: 'Xray.modelDetails.noActiveDocumentsInScope',
                      defaultMessage: 'No documents in scope',
                      description:
                        'Title for the toast when no documents are in scope',
                    })
                  : undefined,
                message: noDocsInScopeMessage,
                status: 'warning',
                onDismiss: noop,
              });
            }
          }
          if (modelVersion.optimizeState) {
            dispatch(
              slices.xRay.actions.setOptimize({
                modelId,
                version: modelVersion.version,
              }),
            );
          }
        }
      })
      .catch(() => {
        toast({
          message: RETRY_ERROR,
          status: 'danger',
        });
      });

  const handlePublish = () => {
    if (
      (!isTableModel &&
        activeModelVersion &&
        activeModelVersion.testCases.length <
          RECOMMENDED_PUBLISH_TEST_CASE_COUNT) ||
      (isTableModel &&
        activeTableModelVersion &&
        activeTableModelVersion.testCases.length <
          RECOMMENDED_PUBLISH_TEST_CASE_COUNT)
    ) {
      showPublishWarningModal();
    } else {
      showEnablePublishModal();
    }
  };

  const onCreditSummary = (credit: CreditSummary) =>
    dispatch(slices.xRay.actions.setCreditsSummary(credit));

  const primaryAction = enableWelcomeContent
    ? {
        text: intl.formatMessage({
          id: 'Xray.modelDetails.getStarted',
          defaultMessage: 'Get Started',
          description:
            'Label for the get started button in the model details section',
        }),
        onClick: () => {
          setHasViewedWelcomeContent(true);
          dispatch(slices.xRay.actions.setHasViewedWelcomeContent(true));
        },
      }
    : Boolean(disableEditOnActiveModels)
      ? {
          text: intl.formatMessage({
            id: 'Xray.modelDetails.editModel',
            defaultMessage: 'Edit Model',
            description:
              'Label for the edit model button in the model details section',
          }),
          onClick: showEditWarningModal,
        }
      : {
          disabled: Boolean(disablePublishTooltip),
          tooltip: disablePublishTooltip,
          text: intl.formatMessage({
            id: 'Xray.modelDetails.reviewModel1',
            defaultMessage: 'Review & Publish',
            description: 'Label for the review of a model prior to publishing',
          }),
          onClick: handlePublish,
        };

  const placeholderContent = documentViewer.documentVersionId
    ? undefined
    : {
        image: 'empty-no-groups' as const,
        title: intl.formatMessage({
          id: 'Xray.modelDetails.noDocumentsTitle',
          defaultMessage: 'There are no documents to evaluate yet.',
          description:
            'Title for the placeholder when there are no documents to evaluate',
        }),
        description: intl.formatMessage(
          {
            id: 'Xray.modelDetails.noDocumentsDescription',
            defaultMessage:
              'Fill in the {entityType} Model Configuration and click on Evaluate to start evaluating.',
            description:
              'Description for the placeholder when there are no documents to evaluate. Example: entityType is either Table or Field',
          },
          {
            entityType: entityDisplayType,
          },
        ),
      };

  const titlePreface = isNew
    ? intl.formatMessage(
        {
          id: 'Xray.modelDetails.newModelTitle',
          defaultMessage: 'New {entityType} Model',
          description:
            'Title for the model details page. Example: entityType is either Table or Field.',
        },
        { entityType: entityDisplayType },
      )
    : intl.formatMessage(
        {
          id: 'Xray.modelDetails.existingModelTitle',
          defaultMessage: 'Edit {entityType} Model',
          description:
            'Title for the model details page with version number. Example: entityType is either Table or Field.',
        },
        { entityType: entityDisplayType },
      );

  const title = `${titlePreface}${
    !isResolvingTargetEntityDetails && targetEntityDetails?.label
      ? ` - ${targetEntityDetails.label}`
      : ''
  }`;

  const loadingContent = {
    isLoading,
    message: intl.formatMessage({
      id: 'Xray.modelDetails.loadingModelDetails',
      defaultMessage: 'Loading model details…',
      description: 'Message displayed while the model details are loading',
    }),
  };

  const welcomeContent = enableWelcomeContent
    ? {
        title: intl.formatMessage({
          id: 'Xray.modelDetails.welcomeTitle',
          defaultMessage: 'Welcome to Custom AI',
          description:
            'Title for the welcome content in the model details section',
        }),
        description: intl.formatMessage({
          id: 'Xray.modelDetails.welcomeDescription',
          defaultMessage:
            'Build a model to automatically populate data fields across your contracts',
          description:
            'Description for the welcome content in the model details section',
        }),

        status: 'ai' as const,
        infoCards: [
          {
            title: intl.formatMessage({
              id: 'Xray.modelDetails.configureModelTitle',
              defaultMessage: 'Configure AI Model',
              description:
                'Title for the info card about configuring the AI model',
            }),
            description: intl.formatMessage({
              id: 'Xray.modelDetails.configureModelDescription',
              defaultMessage:
                'Build an AI model to find the information you need across documents',
              description:
                'Description for the info card about configuring the AI model',
            }),
            number: 1,
          },
          {
            title: intl.formatMessage({
              id: 'Xray.modelDetails.evaluatePerformanceTitle',
              defaultMessage: 'Evaluate Model Performance',
              description:
                'Title for the info card about evaluating model performance',
            }),
            description: intl.formatMessage({
              id: 'Xray.modelDetails.evaluatePerformanceDescription',
              defaultMessage:
                'Review the data identified by the AI to find areas of improvement',
              description:
                'Description for the info card about evaluating model performance',
            }),
            number: 2,
          },
          {
            title: intl.formatMessage({
              id: 'Xray.modelDetails.improvePerformanceTitle',
              defaultMessage: 'Improve Model Performance',
              description:
                'Title for the info card about improving model performance',
            }),
            description: intl.formatMessage({
              id: 'Xray.modelDetails.improvePerformanceDescription',
              defaultMessage:
                'Improve the model’s accuracy by modifying the instructions for the model',
              description:
                'Description for the info card about improving model performance',
            }),
            number: 3,
          },
          {
            title: intl.formatMessage({
              id: 'Xray.modelDetails.publishModelTitle',
              defaultMessage: 'Publish AI Model',
              description:
                'Title for the info card about publishing the AI model',
            }),
            description: intl.formatMessage({
              id: 'Xray.modelDetails.publishModelDescription',
              defaultMessage:
                'Run the model across both existing and new contracts for your organization to populate associated data fields',
              description:
                'Description for the info card about publishing the AI model',
            }),
            number: 4,
          },
        ],
        onDismiss: toggleDismissWelcomeContent,
      }
    : undefined;

  const disabled =
    !!activeModelVersion &&
    testIsActivePublishStatus(activeModelVersion?.publishStatus);

  const modelConfigInternalId = activeModelVersion?.config.internal;

  const moreActions = !enableWelcomeContent
    ? [
        {
          disabled: !modelConfigInternalId,
          onClick: () => {
            copy(modelConfigInternalId!);
            toast({
              message: intl.formatMessage({
                id: 'Xray.modelDetails.copyConfigIdMessage',
                defaultMessage: 'Configuration ID copied to clipboard.',
                description:
                  'Message displayed when the configuration ID is copied',
              }),
              status: 'success',
            });
          },
          text: intl.formatMessage({
            id: 'Xray.modelDetails.copyConfigId',
            defaultMessage: 'Copy Configuration ID',
            description: 'Label for the copy configuration ID menu action',
          }),
          tooltip: modelConfigInternalId
            ? intl.formatMessage({
                id: 'Xray.modelDetails.copyConfigIdTooltip',
                defaultMessage:
                  "Copies the model's system configurations. Does not include instructions, or field options, or filters.",
                description:
                  'Tooltip for the copy configuration ID menu action',
              })
            : intl.formatMessage({
                id: 'Xray.modelDetails.noConfigIdAvailableTooltip',
                defaultMessage: 'No Configuration ID available',
                description:
                  'Tooltip for the copy configuration ID menu action when no ID is available',
              }),
          variant: 'tertiary',
        },
        {
          text: intl.formatMessage({
            id: 'Xray.modelDetails.updateConfigId',
            defaultMessage: 'Update Configuration ID',
            description: 'Label for the update configuration ID menu action',
          }),
          variant: 'tertiary',
          onClick: showPopover,
          disabled,
          tooltip: disabled
            ? intl.formatMessage({
                id: 'Xray.modelDetails.updateConfigIdDisabled',
                defaultMessage: 'The model is not in an editing status',
                description:
                  'Tooltip for update config ID when model is not editable',
              })
            : undefined,
        },
      ]
    : [];

  const handleOnHide = () => {
    setSelectedConfigId(null);
    hidePopover();
    setIsValidConfigStatus(null);
  };

  const [
    checkConfig,
    {
      isLoading: isLoadingCheckConfigId,
      error: errorValidatingCondigId,
      isSuccess: isValidConfigId,
    },
  ] = api.endpoints.checkPromptModelConfig.useMutation();

  useEffect(() => {
    if (isValidConfigId) {
      setIsValidConfigStatus('success');
    } else if (errorValidatingCondigId) {
      setIsValidConfigStatus('failed');
    } else {
      setIsValidConfigStatus(null);
    }
  }, [isValidConfigId, errorValidatingCondigId]);

  /** Util to update the active test case highlights.
   * @param updatedTestCase - The updated test case.
   * @param highlight - The highlight to update.
   * @param isNew - Flag to determine if the highlight is new. Defaults to false.
   */
  const handleUpdateTestCaseHighlights = (
    updatedTestCase: PromptModelTestCase,
    highlight: Highlight,
    { isNew = false } = {},
  ) => {
    const newHighlight =
      isNew && updatedTestCase.goldValue?.highlights?.length
        ? updatedTestCase.goldValue.highlights[
            updatedTestCase.goldValue.highlights.length - 1
          ]
        : highlight;
    dispatch(
      slices.xRay.actions.setDocumentViewer({
        documentId: documentViewer.documentId,
        documentVersionId: documentViewer.documentVersionId,
        activeHighlightId: newHighlight.id,
        highlights: isNew ? [newHighlight] : [],
      }),
    );

    dispatch(slices.xRay.actions.setUpdatedTestCase(updatedTestCase));
  };

  const isPreviousVersion = !(activeModelVersion?.version === latestVersion);

  /** Tagging hook */
  const { getHighlightClickActions, getHighlightCreateActions } =
    useGetHighlightActions({
      activeTestCase,
      config,
      disableEdit: Boolean(disableEditOnActiveModels),
      isPreviousVersion,
      isTableModel,
      modelId,
      onUpdateTestCaseHighlights: handleUpdateTestCaseHighlights,
      onToast: toast,
    });

  const handleNavigateToModelLibrary = () => {
    if (isConfigDirty) {
      setOnConfirmDiscardChangesSearchParam({
        key: 'source',
        value: modelId,
        pathname: RoutePathType.XRayLibrary,
      });
      showConfirmDiscardChangesModal();
    } else {
      setSearchParam('source', modelId, {
        pathname: RoutePathType.XRayLibrary,
      });
    }
  };

  // only enable optimize if 1) flag is enabled, 2) there is an optimize, 3) Not publishing/published.
  const enableOptimize =
    enableDocumentXrayOptimize &&
    optimize &&
    !testIsActivePublishStatus(activeModelVersion?.publishStatus ?? null);

  const [saveDraft] = api.endpoints.savePromptModelDraft.useMutation();

  const handleToggleIsExactTextMatch = (
    updatedEnableTextExactMatch: boolean,
  ) => {
    if (activeModelVersion) {
      saveDraft({
        config: {
          ...config,
          stringMatchingLevel: updatedEnableTextExactMatch
            ? StringMatchingLevel.EXACT
            : StringMatchingLevel.MATCH_LLM_VALUES,
        },
        modelId: activeModelVersion.id,
        highlightMethod,
      })
        .unwrap()
        .catch(() => {
          toast({
            message: intl.formatMessage({
              id: 'Evaluate.failedToApplyExactMatch',
              defaultMessage: 'Failed to apply exact match',
              description:
                'Error message when exact match setting fails to save',
            }),
            status: 'danger',
          });
        });
    }
  };

  const panel = {
    children: (
      <Layout pb={12} preset="sections">
        <Popover
          containerStyle={{
            margin: '0',
            padding: '0',
            marginLeft: '300px',
            marginTop: '-90px',
            marginBottom: '64px',
          }}
          children={
            <Layout preset="form-fields">
              <FormField<string, false>
                description={intl.formatMessage({
                  id: 'Xray.modelDetails.CopyConfigIdDescription',
                  defaultMessage:
                    'Paste in the Configuration ID from other field models.',
                  description: 'A description on how to paste a config ID',
                })}
                input={TextInput}
                footer={intl.formatMessage({
                  id: 'Xray.modelDetails.copyConfigIdValidation',
                  defaultMessage: 'Press Enter to submit',
                  description:
                    'Instructions for submitting the config ID to check validity',
                })}
                inputProps={{
                  isMulti: false,
                }}
                error={
                  isValidConfigStatus === 'failed'
                    ? intl.formatMessage({
                        id: 'Xray.modelDetails.copyConfigIdInvalid',
                        defaultMessage: 'Invalid Configuration ID',
                        description: 'Error message for invalid config ID',
                      })
                    : undefined
                }
                success={
                  isValidConfigStatus === 'success'
                    ? intl.formatMessage({
                        id: 'Xray.modelDetails.copyConfigIdValid',
                        defaultMessage: 'ID Validated!',
                        description: 'Success message for valid config ID',
                      })
                    : undefined
                }
                key="model-internal-config-id"
                label={intl.formatMessage({
                  id: 'Xray.modelDetails.updateConfigId',
                  defaultMessage: 'Update Configuration ID',
                  description:
                    'Label for the update configuration ID menu action',
                })}
                name="Update Configuration ID"
                onChange={setSelectedConfigId}
                placeholder={intl.formatMessage({
                  id: 'Xray.modelDetails.pasteConfigIdPlaceholder',
                  defaultMessage: 'Paste Configuration ID',
                  description:
                    'Placeholder for the update config ID input field',
                })}
                value={selectedConfigId ?? ''}
              />
              <Box
                sx={{
                  flex: 'auto',
                }}
              >
                <Button
                  text={intl.formatMessage({
                    id: 'Xray.modelDetails.enterConfigIdButton',
                    defaultMessage: 'Enter',
                    description:
                      'Label for the button to enter the Configuration ID',
                  })}
                  icon="arrow-back"
                  iconPosition="left"
                  isLoading={isLoadingCheckConfigId}
                  variant="primary"
                  disabled={!selectedConfigId}
                  tooltip={
                    !selectedConfigId
                      ? intl.formatMessage({
                          id: 'Xray.modelDetails.enterConfigIdTooltip',
                          defaultMessage: 'Please enter a Configuration ID',
                          description:
                            'Tooltip shown when the Configuration ID input is empty',
                        })
                      : undefined
                  }
                  onClick={() => {
                    const updatedConfigId = {
                      ...config,
                      internal: selectedConfigId,
                    };
                    checkConfig({ config: updatedConfigId })
                      .unwrap()
                      .then(() => {
                        dispatch(
                          slices.xRay.actions.setConfiguration({
                            ...config,
                            internal: selectedConfigId,
                          }),
                        );
                      });
                  }}
                />
              </Box>
            </Layout>
          }
          isVisible={enableTogglePopover}
          onClickOutside={handleOnHide}
          onHide={handleOnHide}
          position="absolute"
          preset="popover"
        />
        {isTableModel ? (
          <TableDetailsPanel
            modelVersion={tableModelVersion ?? null}
            entityId={tableModel?.targetEntity?.id ?? null}
            isConfigDirty={isTableConfigDirty}
          />
        ) : (
          <>
            <Configuration
              currentOptimizeVersion={model?.currentOptimizeInfo}
              isConfigDirty={isConfigDirty}
              modelVersion={activeModelVersion}
              readOnly={Boolean(disableEditOnActiveModels)}
              targetEntityDetails={targetEntityDetails}
              onExit={handleNavigateToModelLibrary}
            />
            {activeModelVersion && (
              <Evaluate
                isConfigDirty={isConfigDirty}
                isExactTextMatch={
                  activeModelVersion.config.stringMatchingLevel ===
                  StringMatchingLevel.EXACT
                }
                isTextField={
                  activeModelVersion.config.field.type === 'STRING' ||
                  activeModelVersion.config.field.type === 'TEXT_AREA'
                }
                modelId={activeModelVersion.id}
                readOnly={Boolean(disableEditOnActiveModels)}
                sectionTitle={intl.formatMessage(
                  {
                    id: 'Xray.modelDetails.evaluateSectionTitle',
                    defaultMessage: 'Step 2: Evaluate {entityType} Model',
                    description:
                      'Title for the evaluate documents section in the model details. Example: entityType is either Table or Field',
                  },
                  { entityType: entityDisplayType },
                )}
                testCases={activeModelVersion.testCases}
                version={activeModelVersion.version}
                onToggleIsExactTextMatch={handleToggleIsExactTextMatch}
              />
            )}
            {enableDocumentXrayOptimize && activeModelVersion && (
              <Box
                styleProps={{
                  bottom: 0,
                  left: 0,
                  position: 'absolute',
                  right: 0,
                }}
                styles={styles.position}
              >
                <BoostTrigger
                  modelVersion={activeModelVersion}
                  readOnly={Boolean(disableEditOnActiveModels)}
                />
              </Box>
            )}
          </>
        )}
      </Layout>
    ),
    loadingContent,
    mode: 'inline' as const,
    moreActions,
    position: enableCopilot ? ('left' as const) : ('right' as const),
    title: intl.formatMessage(
      {
        id: 'Xray.modelDetails.modelSectionTitle',
        defaultMessage: 'Step 1: {entityType} Model Configuration',
        description:
          'Title for the table model configuration section in model details. Example: entityType is either Table or Field',
      },
      { entityType: entityDisplayType },
    ),
    welcomeContent,
    width: 'm' as const,
  };

  return (
    <>
      {enableOptimize ? (
        <BoostModal field={field} onRefetch={refetchFieldModel} />
      ) : (
        <Modal
          cancelText={intl.formatMessage({
            id: 'Xray.modelDetails.cancel',
            defaultMessage: 'Close',
            description: 'Label for  closing the model modal',
          })}
          disableHideOnEscape
          enableContentPadding={false}
          headerCalloutContent={
            !isLoading &&
            enableDocumentXRayBilling &&
            !enableXrayNewPricingModel &&
            enableCopilot &&
            !isNew ? (
              <>
                <AskAnythingButton />
                <CreditBalance
                  clientId={clientId}
                  estimateUnit={{
                    modelId: isNew ? undefined : modelId,
                    isLoadingUnitValue: isFetchingDocsInScope,
                    isUninitialized,
                    unit: 'document-count',
                    value: docsInScope,
                  }}
                  getCreditsTooltip={useGetCreditsTooltip}
                  getEstimatedCreditsTooltip={
                    field
                      ? useGetEstimatedCreditsTooltip
                      : () =>
                          intl.formatMessage({
                            id: 'Xray.modelDetails.noFieldSelected',
                            defaultMessage:
                              'Please select a field to get an estimation.',
                            description:
                              'Tooltip shown when no field is selected for credit estimation',
                          })
                  }
                  onCreditsSummaryAvailable={onCreditSummary}
                />
              </>
            ) : !isLoading &&
              enableDocumentXRayBilling &&
              !enableXrayNewPricingModel ? (
              <CreditBalance
                clientId={clientId}
                estimateUnit={{
                  modelId: isNew ? undefined : modelId,
                  isLoadingUnitValue: isFetchingDocsInScope,
                  isUninitialized,
                  unit: 'document-count',
                  value: docsInScope,
                }}
                getCreditsTooltip={useGetCreditsTooltip}
                getEstimatedCreditsTooltip={
                  field
                    ? useGetEstimatedCreditsTooltip
                    : () =>
                        intl.formatMessage({
                          id: 'Xray.modelDetails.noFieldSelected',
                          defaultMessage:
                            'Please select a field to get an estimation.',
                          description:
                            'Tooltip shown when no field is selected for credit estimation',
                        })
                }
                onCreditsSummaryAvailable={onCreditSummary}
              />
            ) : enableCopilot && !isNew ? (
              <AskAnythingButton />
            ) : null
          }
          icon={
            !isResolvingTargetEntityDetails &&
            activeModelVersion?.libraryModelId
              ? {
                  icon: 'library' as const,
                  iconPosition: 'right',
                  tooltip: intl.formatMessage({
                    id: 'Xray.modelDetails.libraryModelIconTooltip',
                    defaultMessage: 'From model library',
                    description: 'Tooltip for model from library icon',
                  }),
                }
              : undefined
          }
          isFullPage
          isVisible
          leftActions={
            enableXRayLibrary
              ? [
                  {
                    text: intl.formatMessage({
                      id: 'Xray.modelDetails.browseModelLibrary',
                      defaultMessage: 'Browse Model Library',
                      description:
                        'Label for the button to browse the model library',
                    }),
                    icon: 'library' as const,
                    iconPosition: 'left' as const,
                    level: 'tertiary' as const,
                    onClick: handleNavigateToModelLibrary,
                  },
                ]
              : undefined
          }
          loadingContent={loadingContent}
          panel={panel}
          primaryAction={primaryAction}
          title={title}
          chips={
            activeModelVersion && !isLoading && progress
              ? enablePublishIroning
                ? [
                    getModelProgressStatusChip({
                      optimizeState: activeModelVersion.optimizeState,
                      publishStatus: activeModelVersion.publishStatus,
                      docs: {
                        processed: progress.docsProcessed,
                        inScope: progress.docsInScope,
                        failed: progress.docsFailed,
                      },
                      intl,
                    }),
                  ]
                : [
                    DEPRECATED_getModelProgressStatusChip({
                      optimizeState: activeModelVersion.optimizeState,
                      publishStatus: activeModelVersion.publishStatus,
                      intl,
                    }),
                  ]
              : undefined
          }
          onCancel={handleCancel}
        >
          <DocumentViewer
            {...documentViewer}
            placeholderContent={placeholderContent}
            onHighlightClick={{
              getActions: getHighlightClickActions,
            }}
            onHighlightCreate={{
              getActions: getHighlightCreateActions,
            }}
            options={{
              // different handlers points to the same original document, hence no need to fetch the latest version
              autoLatestVersion: !isCustomAiAsyncTestCreationExecutionEnabled,
            }}
          />
        </Modal>
      )}
      {enableXrayNewPricingModel &&
      isTableModel &&
      tableModel &&
      activeTableModelVersion ? (
        <PublishQuotaModelVersionModal
          isVisible={enablePublishModal}
          modelVersion={activeTableModelVersion}
          targetEntity={{
            id: tableModel.targetEntity.id,
            label: tableConfig.tableSpecState.name,
            type: EntityType.DocumentAttachedData,
          }}
          onCancel={() => {
            hideEnablePublishModal();
            hidePublishWarningModal();
          }}
        />
      ) : enableXrayNewPricingModel && !isTableModel && activeModelVersion ? (
        <PublishQuotaFieldModelModal
          isVisible={enablePublishModal}
          fieldModel={activeModelVersion}
          onCancel={() => {
            hideEnablePublishModal();
            hidePublishWarningModal();
          }}
        />
      ) : (
        <></>
      )}
      {!enableXrayNewPricingModel &&
      isTableModel &&
      tableModel &&
      activeTableModelVersion ? (
        <PublishModelVersionModal
          isVisible={enablePublishModal}
          modelVersion={activeTableModelVersion}
          targetEntity={{
            id: tableModel.targetEntity.id,
            label: tableConfig.tableSpecState.name,
            type: EntityType.DocumentAttachedData,
          }}
          onCancel={() => {
            hideEnablePublishModal();
            hidePublishWarningModal();
          }}
        />
      ) : !enableXrayNewPricingModel && !isTableModel && activeModelVersion ? (
        <PublishFieldModelModal
          isVisible={enablePublishModal}
          fieldModel={activeModelVersion}
          onCancel={() => {
            hideEnablePublishModal();
            hidePublishWarningModal();
          }}
        />
      ) : (
        <></>
      )}
      {confirmDiscardChangesModal}
      {!enablePublishModal && publishWarningModal}
      {editWarningdModal}
    </>
  );
};
